import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Crop } from "@/types/crop";
import { Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { isDuplicateName } from "@/lib/utils";

interface EditCropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  crop: Crop | null;
  onSubmit: (data: { name: string; description?: string }) => Promise<void>;
  onDelete: (cropId: string) => Promise<void>;
  canDelete?: boolean;
  existingNames?: string[];
}

const EditCropDialog: React.FC<EditCropDialogProps> = ({
  open,
  onOpenChange,
  crop,
  onSubmit,
  onDelete,
  canDelete = false,
  existingNames,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const hasDuplicateName = existingNames ? isDuplicateName(name, existingNames, crop?.name) : false;

  useEffect(() => {
    if (crop) {
      setName(crop.name);
      setDescription(crop.description || "");
    }
  }, [crop]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!crop?.id) return;
    // Don't manually close the AlertDialog - let Radix's AlertDialogAction
    // handle it via onOpenChange to ensure proper body pointer-events cleanup.
    await onDelete(crop.id);
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  if (!crop) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Crop</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  id="edit-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter crop name"
                  required
                  className={hasDuplicateName && !isSubmitting ? "border-destructive focus-visible:ring-destructive" : ""}
                />
                {hasDuplicateName && !isSubmitting && (
                  <p className="text-sm text-destructive">
                    A crop with this name already exists. Delete the existing one or use a different name.
                  </p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter a description (optional)"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter className="flex justify-between sm:justify-between">
              {canDelete && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="mr-auto"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!name.trim() || isSubmitting || hasDuplicateName}>
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Crop</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{crop?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EditCropDialog;
